import { relations, sql } from 'drizzle-orm';
import { boolean, date, decimal, index, integer, pgEnum, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { addresses } from './addresses.js';
import { user } from './auth.js';
import { sections, wards } from './locations.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
import { customers } from './customers.js';
import { diagnoses, generalPhysicians, insuranceCompanies } from './basic-data.js';
import { bsaFormulaEnum, ibwFormulaEnum } from './settings.js';
import { gfrMethodEnum, serumUnitEnum } from './enums.js';
export const Title = z.enum(['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'rev']);
export const titleEnum = pgEnum('title', [
    Title.enum.mr,
    Title.enum.mrs,
    Title.enum.ms,
    Title.enum.miss,
    Title.enum.dr,
    Title.enum.prof,
    Title.enum.rev,
]);
export const Gender = z.enum(['male', 'female', 'other', 'unknown']);
export const genderEnum = pgEnum('gender', [
    Gender.enum.male,
    Gender.enum.female,
    Gender.enum.other,
    Gender.enum.unknown,
]);
export const Sex = z.enum(['male', 'female']);
export const sexEnum = pgEnum('sex', [Sex.enum.male, Sex.enum.female]);
export const MaritalStatus = z.enum(['single', 'married', 'divorced', 'widowed', 'separated']);
export const maritalStatusEnum = pgEnum('marital_status', [
    MaritalStatus.enum.single,
    MaritalStatus.enum.married,
    MaritalStatus.enum.divorced,
    MaritalStatus.enum.widowed,
    MaritalStatus.enum.separated,
]);
/**
 * Defines how the patient was added in the database.
 *
 * - `manual` means the patient was added manually through cypro UI.
 * - `hl7_message` means the patient was added automatically through a message received from HL7, but the patient was not confirmed yet by the admin.
 * - `hl7_import` means the patient is a former `hl7-message` and confirmed by the admin, and should be treated equally to the `manual` patients.
 */
export const PatientSource = z.enum(['manual', 'hl7_message', 'hl7_import']);
export const patientSourceEnum = pgEnum('patient_source', [
    PatientSource.enum.manual,
    PatientSource.enum.hl7_message,
    PatientSource.enum.hl7_import,
]);
export const patients = pgTable('patient', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    nationalId: text('national_id'),
    familyName: text('family_name').notNull(),
    givenName: text('given_name').notNull(),
    /**
     * Lowercased, diacritics-free copy of givenName, used exclusively for case-insensitive ORDER BY.
     * Zero/ZQL does not support raw SQL expressions (LOWER(), etc.) in .orderBy(), so we store a pre-normalized sort column alongside the display column.
     */
    sortableGivenName: text('sortable_given_name').notNull(),
    /**
     * Lowercased, diacritics-free copy of familyName, used exclusively for case-insensitive ORDER BY.
     * Zero/ZQL does not support raw SQL expressions in .orderBy(), so we store a pre-normalized sort column alongside the display column.
     */
    sortableFamilyName: text('sortable_family_name').notNull(),
    fullName: text('full_name'),
    title: titleEnum('title'),
    birthDate: date('birth_date'),
    sex: sexEnum('sex'),
    gender: genderEnum('gender'),
    maritalStatus: maritalStatusEnum('marital_status'),
    nationality: text('nationality'),
    deceasedDate: timestamp('deceased_date'),
    isTestData: boolean('is_test_data').notNull().default(false),
    // Family doctor (external GP) from the general_physicians basic-data table.
    familyDoctorId: uuid('family_doctor_id').references(() => generalPhysicians.id, { onDelete: 'set null' }),
    // Responsible doctor for the prescribed treatment; a Cypro user, internal or external.
    responsibleDoctorId: uuid('responsible_doctor_id').references(() => user.id, { onDelete: 'set null' }),
    hospitalPatientId: text('hospital_patient_id'),
    searchText: text('search_text'),
    barcodePrintFlag: boolean('barcode_print_flag'),
    notes: text('notes'),
    source: patientSourceEnum('source').default(PatientSource.enum.manual),
    ...timestamps(),
    ...userActions(),
}, table => [
    // Partial index for deletedAt IS NULL
    index('idx_patients_active')
        .on(table.deletedAt)
        .where(sql `${table.deletedAt} IS NULL`),
    // Composite cursor indexes for pagination
    index('idx_patients_cursor_desc')
        .on(table.createdAt.desc(), table.id.desc())
        .where(sql `${table.deletedAt} IS NULL`),
    index('idx_patients_cursor_asc')
        .on(table.createdAt.asc(), table.id.asc())
        .where(sql `${table.deletedAt} IS NULL`),
    // Physician and source filters
    index('idx_patients_responsible_doctor')
        .on(table.responsibleDoctorId)
        .where(sql `${table.deletedAt} IS NULL`),
    index('idx_patients_source')
        .on(table.source)
        .where(sql `${table.deletedAt} IS NULL`),
    // GIN trigram index for ILIKE search on searchText
    index('idx_patients_search_text').using('gin', sql `${table.searchText} gin_trgm_ops`),
    // the patient list is sortable by name/birth date with cursor pagination
    index('idx_patients_sortable_given_name')
        .on(table.sortableGivenName, table.id)
        .where(sql `${table.deletedAt} IS NULL`),
    index('idx_patients_sortable_family_name')
        .on(table.sortableFamilyName, table.id)
        .where(sql `${table.deletedAt} IS NULL`),
    index('idx_patients_birth_date')
        .on(table.birthDate, table.id)
        .where(sql `${table.deletedAt} IS NULL`),
    // report filters search these identifiers with ILIKE
    index('idx_patients_hospital_patient_id_trgm').using('gin', sql `${table.hospitalPatientId} gin_trgm_ops`),
    index('idx_patients_national_id_trgm').using('gin', sql `${table.nationalId} gin_trgm_ops`),
]);
export const patientRelations = relations(patients, ({ one, many }) => ({
    created: one(user, {
        fields: [patients.createdBy],
        references: [user.id],
    }),
    familyDoctor: one(generalPhysicians, {
        fields: [patients.familyDoctorId],
        references: [generalPhysicians.id],
    }),
    responsibleDoctor: one(user, {
        fields: [patients.responsibleDoctorId],
        references: [user.id],
    }),
    identifiers: many(patientIdentifiers),
    contacts: many(patientContacts),
    characteristics: many(patientCharacteristics),
    encounters: many(patientEncounters),
    insurance: one(patientInsurances, {
        fields: [patients.id],
        references: [patientInsurances.patientId],
    }),
    gfrs: many(patientGfrs),
}));
export const IdentifierUse = z.enum(['usual', 'official', 'temp', 'secondary', 'old']);
export const identifierUseEnum = pgEnum('identifier_use', [
    IdentifierUse.enum.usual,
    IdentifierUse.enum.official,
    IdentifierUse.enum.temp,
    IdentifierUse.enum.secondary,
    IdentifierUse.enum.old,
]);
export const patientIdentifiers = pgTable('patient_identifier', {
    id: uuidv7('id').primaryKey(),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade' }),
    system: text('system').notNull(),
    systemName: text('system_name'),
    value: text('value').notNull(),
    use: identifierUseEnum('use'),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_patient_identifier_patient').on(t.patientId, t.system, t.createdAt)]);
export const patientIdentifierRelations = relations(patientIdentifiers, ({ one }) => ({
    patient: one(patients, {
        fields: [patientIdentifiers.patientId],
        references: [patients.id],
    }),
    created: one(user, {
        fields: [patientIdentifiers.createdBy],
        references: [user.id],
    }),
}));
export const ContactType = z.enum(['home', 'work', 'emergency']);
export const contactTypeEnum = pgEnum('contact_type', [
    ContactType.enum.home,
    ContactType.enum.work,
    ContactType.enum.emergency,
]);
// https://hl7-definition.caristix.com/v2/HL7v2.4/Fields/PID.11
export const patientContacts = pgTable('patient_contact', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade' }),
    contactType: contactTypeEnum('contact_type'),
    name: text('name'),
    relationship: text('relationship'),
    phone: text('phone'),
    businessPhone: text('business_phone'),
    email: text('email'),
    addressId: uuid('address_id').references(() => addresses.id, { onDelete: 'set null' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_patient_contact_patient').on(t.patientId, t.contactType, t.createdAt)]);
export const patientContactRelations = relations(patientContacts, ({ one }) => ({
    patient: one(patients, {
        fields: [patientContacts.patientId],
        references: [patients.id],
    }),
    address: one(addresses, {
        fields: [patientContacts.addressId],
        references: [addresses.id],
    }),
    created: one(user, {
        fields: [patientContacts.createdBy],
        references: [user.id],
    }),
}));
export const patientClassEnum = pgEnum('patient_class', ['inpatient', 'outpatient', 'emergency', 'observation']);
export const admissionTypeEnum = pgEnum('admission_type', ['emergency', 'urgent', 'elective', 'newborn', 'trauma']);
export const encounterStatusEnum = pgEnum('encounter_status', ['active', 'cancelled']);
export const patientEncounters = pgTable('patient_encounter', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade' }),
    sectionId: uuid('section_id').references(() => sections.id, { onDelete: 'set null' }),
    wardId: uuid('ward_id').references(() => wards.id, { onDelete: 'set null' }),
    physicianId: uuid('physician_id').references(() => user.id, { onDelete: 'set null' }),
    customerId: uuid('customer_id').references(() => customers.id, { onDelete: 'set null' }),
    ambulatory: boolean('ambulatory'),
    startedAt: timestamp('started_at'),
    endedAt: timestamp('ended_at'),
    reason: text('reason'),
    // HL7 PV1 additions
    visitNumber: text('visit_number').notNull(), // PV1-19, external encounter/admission ID (e.g. AMMS)
    patientClass: patientClassEnum('patient_class'), // PV1-2
    admissionType: admissionTypeEnum('admission_type'), // PV1-4
    pointOfCare: text('point_of_care'), // PV1-3 (ward/department code)
    room: text('room'), // PV1-3 (room)
    bed: text('bed'), // PV1-3 (bed)
    locationDescription: text('location_description'), // PV1-3 (description)
    status: encounterStatusEnum('status').default('active'),
    // Raw PV1 segment text, retained for outbound replay compatibility (e.g. legacy ASSECO_PV1_RECORD).
    rawPv1: text('raw_pv1'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_patient_encounter_patient').on(t.patientId, t.createdAt),
    // report filters by ward/section use the latest encounter
    index('idx_patient_encounter_ward').on(t.wardId),
    index('idx_patient_encounter_section').on(t.sectionId),
]);
export const patientEncounterRelations = relations(patientEncounters, ({ one }) => ({
    patient: one(patients, {
        fields: [patientEncounters.patientId],
        references: [patients.id],
    }),
    section: one(sections, {
        fields: [patientEncounters.sectionId],
        references: [sections.id],
    }),
    ward: one(wards, {
        fields: [patientEncounters.wardId],
        references: [wards.id],
    }),
    customer: one(customers, {
        fields: [patientEncounters.customerId],
        references: [customers.id],
    }),
    created: one(user, {
        fields: [patientEncounters.createdBy],
        references: [user.id],
    }),
}));
export const patientInsurances = pgTable('patient_insurance', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade' })
        .unique(),
    country: text('country'),
    companyId: uuid('company_id'),
    insuranceNumber: text('insurance_number'),
    policyNumber: text('policy_number'),
    expirationDate: date('expiration_date'),
    privatePatient: boolean('private_patient'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_patient_insurance_company').on(t.companyId),
    // duplicate checks and the HL7 match panel search the insurance number with ILIKE
    index('idx_patient_insurance_number_trgm').using('gin', sql `${t.insuranceNumber} gin_trgm_ops`),
]);
export const patientInsuranceRelations = relations(patientInsurances, ({ one }) => ({
    patient: one(patients, {
        fields: [patientInsurances.patientId],
        references: [patients.id],
    }),
    insuranceCompany: one(insuranceCompanies, {
        fields: [patientInsurances.companyId],
        references: [insuranceCompanies.id],
    }),
    created: one(user, {
        fields: [patientInsurances.createdBy],
        references: [user.id],
    }),
}));
export const DiabetesSchema = z.enum(['type1', 'type2', 'none']);
export const diabetesEnum = pgEnum('diabetes', [
    DiabetesSchema.enum.type1,
    DiabetesSchema.enum.type2,
    DiabetesSchema.enum.none,
]);
export const patientCharacteristics = pgTable('patient_characteristic', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade' }),
    height: decimal('height', { mode: 'number' }),
    weight: decimal('weight', { mode: 'number' }),
    gfr: decimal('gfr', { mode: 'number' }),
    bsa: decimal('bsa', { mode: 'number' }),
    bsaFormula: bsaFormulaEnum('bsa_formula'),
    aibw: decimal('aibw', { mode: 'number' }),
    aibwFormula: ibwFormulaEnum('aibw_formula'),
    diabetes: diabetesEnum('diabetes'),
    smoker: boolean('smoker'),
    alcoholic: boolean('alcoholic'),
    useAibw: boolean('use_aibw'),
    diagnosisId: uuid('diagnosisId'),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_patient_characteristic_patient').on(t.patientId, t.createdAt)]);
export const patientCharacteristicRelations = relations(patientCharacteristics, ({ one }) => ({
    patient: one(patients, {
        fields: [patientCharacteristics.patientId],
        references: [patients.id],
    }),
    diagnosis: one(diagnoses, {
        fields: [patientCharacteristics.diagnosisId],
        references: [diagnoses.id],
    }),
    created: one(user, {
        fields: [patientCharacteristics.createdBy],
        references: [user.id],
    }),
}));
export const patientGfrs = pgTable('patient_gfr', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade' }),
    labTime: timestamp('lab_time').notNull(),
    gfr: decimal('gfr', { mode: 'number' }).notNull(),
    method: gfrMethodEnum('method').notNull(),
    serumCreatinine: decimal('serum_creatinine', { mode: 'number' }),
    serumUnit: serumUnitEnum('serum_unit'),
    urineCreatinine: decimal('urine_creatinine', { mode: 'number' }),
    urineVolume: decimal('urine_volume', { mode: 'number' }),
    collectionTime: decimal('collection_time', { mode: 'number' }),
    // Patient data at entry time, kept so the value stays traceable after the patient record changes.
    ageMonths: integer('age_months'),
    weight: decimal('weight', { mode: 'number' }),
    height: decimal('height', { mode: 'number' }),
    // shown as BSA at sample for a Jelliffe GFR (CY-102)
    bsa: decimal('bsa', { mode: 'number' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_patient_gfr_patient').on(t.patientId, t.labTime, t.createdAt)]);
export const patientGfrRelations = relations(patientGfrs, ({ one }) => ({
    patient: one(patients, {
        fields: [patientGfrs.patientId],
        references: [patients.id],
    }),
    created: one(user, {
        fields: [patientGfrs.createdBy],
        references: [user.id],
    }),
}));
//# sourceMappingURL=patients.js.map